<template>
  <div class="media-library-item-fi">
    <div
      :class="{
        'media-library-item-fi__media-container': true,
        'media-library-item-fi__media-container--brand-bg': socialMediaLogo || isVideoLink,
        'media-library-item-fi__media-container--no-image':
          (!fileUrl || !isImageFile) && !isVideoLink && !socialMediaLogo,
        'media-library-item-fi--is-newsroom': isNewsroom,
      }"
    >
      <img
        v-if="socialMediaLogo"
        class="media-library-item-fi__media-icon"
        :src="socialMediaLogo"
        :alt="itemTitle"
      />
      <cloud-image-fi
        v-else-if="image || (fileUrl && isImageFile)"
        class="media-library-item-fi__media"
        :src="image || fileUrl"
        :alt="itemTitle"
        :params="cloudImageParams"
      />
      <template v-else>
        <img
          v-if="!isVideoLink"
          :src="placeholderImage"
        />
      </template>
      <div
        v-if="isVideoLink"
        class="media-library-item-fi__video-overlay-button"
      >
        <icon-fi
          class="media-library-item-fi__video-icon"
          icon="youtube-logo"
        />
      </div>
    </div>
    <div class="media-library-item-fi__content">
      <div class="media-library-item-fi__title">
        {{ itemTitle }}
      </div>

      <div
        v-if="fileUrl"
        class="media-library-item-fi__file-size"
      >
        <span v-if="fileSizeInBytes">{{ getFileSizeAndLabel(fileSizeInBytes) }}</span>
        <span>{{ fileExtension }}</span>
      </div>

      <div class="media-library-item-fi__footer">
        <div
          v-if="itemSource"
          class="media-library-item-fi__file-source"
        >
          <template v-if="fileUrl && isImageFile">
            {{ $dict.get('media-Library.Items.Source-Label-Images') }}
          </template>
          <template v-else>
            {{ $dict.get('media-Library.Items.Source-Label-File') }}
          </template>
          {{ itemSource }}
        </div>

        <div class="media-library-item-fi__controls">
          <a
            v-if="externalLink"
            :href="externalLink"
            class="button-fi__icon button-fi__primary"
            target="_blank"
          >
            <icon-fi icon="link" />
          </a>

          <template v-else>
            <button-fi
              v-if="(fileUrl && isImageFile) || isNewsroom"
              class="media-library-item-fi__zoom-in button-fi__icon button-fi__tertiary"
              @click="onZoomImage"
            >
              <icon-fi icon="zoom-in" />
            </button-fi>

            <a
              v-if="downloadUrl"
              :href="downloadUrl"
              class="button-fi__icon button-fi__primary"
              download
              @click="handleDownload"
            >
              <icon-fi icon="download" />
            </a>
          </template>
        </div>
      </div>
    </div>
    <media-library-zoom-image-dialog-fi
      v-if="!disableDialog && downloadUrl"
      ref="mediaLibraryZoomImageDialog"
      :item="item"
    />
  </div>
</template>

<script setup lang="ts">
import { ButtonFi, CloudImageFi, IconFi } from 'atoms';
import { computed, toRef, useTemplateRef } from 'vue';

import MediaLibraryZoomImageDialogFi from '@/components/media-library-zoom-image-dialog-fi/MediaLibraryZoomImageDialogFi.vue';
import { useAssetUrl } from '@/composables/assetUrl';
import { useEventHub } from '@/composables/eventHub';
import { useMediaLibrary } from '@/composables/mediaLibrary';
import theme from '@/plugins/tailwind-theme';

defineOptions({ name: 'MediaLibraryItemFi' });

const props = withDefaults(
  defineProps<{
    item?: Record<string, any>;
    disableDialog?: boolean;
    isNewsroom?: boolean;
  }>(),
  {
    item: () => ({}),
    disableDialog: false,
    isNewsroom: false,
  },
);

const emit = defineEmits<{
  'show-zoom-dialog': [item: Record<string, any>];
}>();

const { getAssetUrl } = useAssetUrl();
const { sendEvent } = useEventHub();
const {
  fileExtension,
  fileUrl,
  fileSizeInBytes,
  isImageFile,
  image,
  itemTitle,
  itemSource,
  getFileSizeAndLabel,
} = useMediaLibrary(toRef(props, 'item'));

const placeholderImage = getAssetUrl('/img/products/placeholder.svg');

const mediaLibraryZoomImageDialog = useTemplateRef('mediaLibraryZoomImageDialog');

const enableYoutubeLogo = computed(() => props.item?.fields?.showYoutubeLogo?.value);
const enableFacebookLogo = computed(() => props.item?.fields?.showFacebookLogo?.value);
const enableInstagramLogo = computed(() => props.item?.fields?.showInstagramLogo?.value);
const enableTwitterLogo = computed(() => props.item?.fields?.showTwitterLogo?.value);

const socialMediaLogo = computed(() => {
  if (enableYoutubeLogo.value) {
    return getAssetUrl('/img/media-library/youtube-logo.svg');
  } else if (enableFacebookLogo.value) {
    return getAssetUrl('/img/media-library/facebook-logo.svg');
  } else if (enableInstagramLogo.value) {
    return getAssetUrl('/img/media-library/instagram-logo.svg');
  } else if (enableTwitterLogo.value) {
    return getAssetUrl('/img/media-library/twitter-logo.svg');
  }
  return null;
});

const externalLink = computed(
  () =>
    props.item?.fields?.externalLink?.value?.href || props.item?.fields?.externalLink?.value?.url,
);

const isVideoLink = computed(() => props.item?.fields?.isVideoLink?.value);

const downloadUrl = computed(() => (props.isNewsroom ? image.value : fileUrl.value));

const cloudImageParams = computed(() => `func=fit&bg_color=${theme.fiwe.grey}&aspect_ratio=16:9`);

const onZoomImage = () => {
  if (props.disableDialog) {
    emit('show-zoom-dialog', props.item);
  } else {
    mediaLibraryZoomImageDialog.value?.show();
  }
};

const handleDownload = () => {
  sendEvent('custom', {}, 'download', {
    link: downloadUrl.value,
  });
};
</script>

<style src="./media-library-item-fi-unscoped" />

<style scoped src="./media-library-item-fi" />
